"use client";
import React, { useState, useMemo } from "react";
import Modal, { ModalFooter } from "~/components/v3/themed/Modal";
import { Input } from "~/components/v3/themed/Input";
import { Button } from "~/components/v3/themed/Button";
import Spinner from "~/components/themed/spinner";
import Fuse from "fuse.js";
import { GetWaitlistCommunities, suggestCommunity } from "../actions";
import { CommunityWaitlistCardsList } from "./CommunityWaitlistCard";
import { toastHandler } from "~/components/ToastHandler";
import { useAutoAnimate } from "@formkit/auto-animate/react";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuggest?: (communityName: string) => Promise<void>;
    communities: GetWaitlistCommunities;
};

const SuggestCommunityModal = ({
    isOpen,
    onClose,
    onSuggest,
    communities,
}: Props) => {
    const [ref] = useAutoAnimate();
    const [searchTerm, setSearchTerm] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmittedSuggestion, setHasSubmittedSuggestion] = useState(false);

    const fuse = useMemo(() => {
        return new Fuse(communities, {
            keys: ["name", "description"],
            threshold: 0.4,
            includeScore: true,
        });
    }, [communities]);

    const filteredCommunities = useMemo(() => {
        if (!searchTerm || searchTerm.length < 3) return communities;
        return fuse.search(searchTerm).map((result) => result.item);
    }, [fuse, searchTerm, communities]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setHasSubmittedSuggestion(false);
    };

    const handleSubmitCustom = async () => {
        if (!searchTerm.trim()) return;
        setIsSubmitting(true);

        await toastHandler({
            loadingMessage: "Submitting your suggestion...",
            successMessage: "Thanks for your suggestion!",
            errorMessage: "Failed to submit your suggestion. Please try again.",
            operation: async () => {
                const result = await suggestCommunity(searchTerm);

                if (!result.success) {
                    throw new Error("Failed to suggest community");
                }

                if (onSuggest) {
                    await onSuggest(searchTerm);
                }

                return result;
            },
            onSuccess: () => {
                setHasSubmittedSuggestion(true);
                setSearchTerm("");

                setTimeout(() => {
                    onClose();
                }, 2000);
            },
            onSettled: () => {
                setIsSubmitting(false);
            },
        });
    };

    const handleCommunitySelection = (communityId: string) => {
        // Logic to handle community selection
        console.log("Selected community:", communityId);
        onClose();
    };

    return (
        <Modal
            open={isOpen}
            onOpenChange={onClose}
            title="Suggest a community"
            className="w-full sm:max-w-[600px]"
            footer={
                <ModalFooter>
                    <Button
                        variant="secondary"
                        disabled={
                            !searchTerm.trim() ||
                            isSubmitting ||
                            hasSubmittedSuggestion
                        }
                        onClick={handleSubmitCustom}
                    >
                        {isSubmitting ? (
                            <Spinner type="small" />
                        ) : hasSubmittedSuggestion ? (
                            "Thanks for your suggestion!"
                        ) : (
                            "Suggest a community"
                        )}
                    </Button>
                </ModalFooter>
            }
        >
            <div className="mt-4 w-full" ref={ref}>
                <p className="mb-4 text-sm text-v3-N300">
                    What communities would you like to hangout with irl?
                </p>
                <Input
                    type="text"
                    placeholder="Search or suggest a community..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="mb-4 w-full"
                />
                {searchTerm.length > 2 ? (
                    filteredCommunities.length > 0 ? (
                        <div className="max-h-[320px] w-full overflow-y-auto">
                            <CommunityWaitlistCardsList
                                communities={filteredCommunities}
                            />
                        </div>
                    ) : (
                        <div className="w-full">
                            {hasSubmittedSuggestion && (
                                <div className="mb-4 text-sm text-green-400">
                                    Thanks for your suggestion! We'll consider
                                    adding this community.
                                </div>
                            )}
                        </div>
                    )
                ) : null}
            </div>
        </Modal>
    );
};

export default SuggestCommunityModal;
